import React, { useState, useEffect, useMemo, useRef } from 'react';

import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { TableCustom, SectionHeading, WrapperAdvancedSearch } from '../../components/common';
import CalendarioModal from '../../components/controls/CalendarioModal';
import { useManagedContext, usePermissions } from '../../components/hooks';
import { useLista } from '../../components/hooks/useLista';
import { useEntidad } from '../../components/hooks/useEntidad';
import { standardRequest } from '../../utils/requests';
import { PERMISSIONS } from '../../consts/permissions';
import { getDateToString, getDateNow } from '../../utils/convert';
import { GetFieldMes } from '../../utils/helpers';
import { PERIODICIDAD_DECLARACION_JURADA } from '../../consts/periodicidadDeclaracionJurada';

function CalendarioView() {
    const { setIsLoading, showMessageModal } = useManagedContext();
    const { hasPermission } = usePermissions();
    const allowEditActions = hasPermission(PERMISSIONS.LISTAS_INGRESOS_PUBLICOS_EDIT);

    const searchRef = useRef();

    const [state, setState] = useState({
        showForm: false,
        rowId: 0,
        list: []
    });

    const [filtros, setFiltros] = useState({
        anio: getDateNow().getFullYear().toString(),
        idTipoDiaCalendario: 0,
        idModeloDeclaracionJurada: 0
    });

    const mount = () => {
        SearchCalendario();
    }
    useEffect(mount, []);

    const [, getRowLista] = useLista({
        listas: ['TipoDiaCalendario'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: { key: 'TipoDiaCalendario', timeout: 0 }
    });

    const [getListEntidad, getRowEntidad, entidadReady] = useEntidad({
        entidades: ['ModeloDeclaracionJurada'],
        onLoaded: (entidades, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: { key: 'ModeloDeclaracionJurada', timeout: 0 }
    });

    const getDescTipoDia = (id) => {
        const row = getRowLista('TipoDiaCalendario', id);
        return (row) ? row.nombre : '';
    }

    const getDescModelo = (id) => {
        const row = getRowEntidad('ModeloDeclaracionJurada', id);
        return (row) ? row.nombre : '';
    }

    const optionsModelo = useMemo(() => {
        const list = getListEntidad('ModeloDeclaracionJurada') ?? [];
        return [
            { value: 0, label: 'Todos' },
            ...list.filter(item => item.activo).map(item => ({ value: item.id, label: item.nombre }))
        ];
    }, [entidadReady]);

    const getPeriodo = (row) => {
        if (!row.idModeloDeclaracionJurada) return '';
        const periodo = {
            [PERIODICIDAD_DECLARACION_JURADA.BIMESTRAL]: 'Bimestre',
            [PERIODICIDAD_DECLARACION_JURADA.TRIMESTRAL]: 'Trimestre',
            [PERIODICIDAD_DECLARACION_JURADA.SEMESTRAL]: 'Semestre'
        }[getRowEntidad('ModeloDeclaracionJurada', row.idModeloDeclaracionJurada)?.idPeriodicidadDeclaracionJurada];
        if (periodo) return `${row.periodoDeclaracion}° ${periodo} ${row.anioDeclaracion}`;
        const mes = GetFieldMes(row.periodoDeclaracion, 'cuota');
        return (mes) ? `${mes}/${row.anioDeclaracion}` : `${row.anioDeclaracion}`;
    }

    const listFiltrada = useMemo(() => state.list.filter(row => {
        if (filtros.anio && row.fecha.getFullYear().toString() !== filtros.anio) return false;
        if (filtros.idTipoDiaCalendario > 0 && Number(row.idTipoDiaCalendario) !== Number(filtros.idTipoDiaCalendario)) return false;
        if (filtros.idModeloDeclaracionJurada > 0 && Number(row.idModeloDeclaracionJurada) !== Number(filtros.idModeloDeclaracionJurada)) return false;
        return true;
    }), [state.list, filtros]);

    const tableColumns = [
        { Header: 'Fecha', Cell: (data) => getDateToString(data.value, false), accessor: 'fecha', width: '12%' },
        { Header: 'Tipo de día', Cell: (data) => getDescTipoDia(data.value), accessor: 'idTipoDiaCalendario', width: '17%' },
        { Header: 'Descripción', accessor: 'descripcion', width: '30%' },
        { Header: 'Modelo', Cell: (data) => getDescModelo(data.value), accessor: 'idModeloDeclaracionJurada', width: '18%' },
        { Header: 'Período', Cell: (data) => getPeriodo(data.row.original), accessor: 'periodoDeclaracion', width: '9%' },
        { Header: 'Laborable', Cell: (data) => (data.value) ? 'Sí' : 'No', accessor: 'laborable', width: '10%' }
    ];

    const avmr = {
        onAdd: () => {
            setState(prevState => ({...prevState, showForm: true, rowId: 0}));
        },
        onModify: ({ id }) => {
            setState(prevState => ({...prevState, showForm: true, rowId: parseInt(id)}));
        },
        onRemove: ({ id }) => {
            const item = state.list.find(x => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar el día ${getDateToString(item.fecha, false)}?`,
                onConfirm: () => {
                    setIsLoading(true);
                    standardRequest(REQUEST_METHOD.DELETE, `${APIS.URLS.CALENDARIO}/${id}`)
                    .then(() => SearchCalendario())
                    .catch(() => setIsLoading(false));
                }
            });
        }
    };

    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
            setIsLoading(false);
        })
        .catch((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
            setIsLoading(false);
        });
    }

    const callbackError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message);
        setIsLoading(false);
    }

    function onSearch() {
        const { anio, idTipoDiaCalendario, idModeloDeclaracionJurada } = searchRef.current?.filters || {};
        setFiltros({
            anio: anio ?? '',
            idTipoDiaCalendario: idTipoDiaCalendario ?? 0,
            idModeloDeclaracionJurada: idModeloDeclaracionJurada ?? 0
        });
    }

    function SearchCalendario() {
        setIsLoading(true);
        setState(prev => ({ ...prev, list: [] }));

        const callbackSuccess = (response) => {
            response.json()
            .then((data) => {
                data.forEach(x => {
                    if (x.fecha) x.fecha = new Date(x.fecha);
                });
                data.sort((a, b) => a.fecha.getTime() - b.fecha.getTime());
                setIsLoading(false);
                setState(prev => ({ ...prev, list: data }));
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                setIsLoading(false);
            });
        };

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CALENDARIO,
            null,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    return (
    <>
        {state.showForm &&
            <CalendarioModal
                id={state.rowId}
                disabled={!allowEditActions}
                onDismiss={() => {
                    setState(prevState => ({...prevState, showForm: false, rowId: 0}));
                }}
                onConfirm={() => {
                    setState(prevState => ({...prevState, showForm: false, rowId: 0}));
                    SearchCalendario();
                }}
            />
        }

        <SectionHeading titles={[{ title: 'Configuración' }, { title: 'Calendario' }]} />

        <section className='section-accordion'>

            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { title: 'Año', field: 'anio', type: 'ejercicio', size: 2, presetValue: getDateNow().getFullYear().toString() },
                    { title: 'Tipo de día', field: 'idTipoDiaCalendario', type: 'selectLista', lista: 'TipoDiaCalendario', size: 3 },
                    { title: 'Modelo', field: 'idModeloDeclaracionJurada', type: 'selectLista', options: optionsModelo, size: 3 }
                ]}
                onSearch={onSearch}
            />

            <div className="m-top-20">

                <TableCustom
                    showFilterGlobal={false}
                    className={'TableCustomBase'}
                    columns={tableColumns}
                    data={listFiltrada}
                    avmr={avmr}
                    editCode={PERMISSIONS.LISTAS_INGRESOS_PUBLICOS_EDIT}
                    disabled={!allowEditActions}
                    messageEmpty={filtros.anio ? `No hay días cargados para ${filtros.anio}` : 'No hay días cargados'}
                />

            </div>

        </section>
    </>
    )
}

export default CalendarioView;
